import { useState, useEffect, useCallback } from 'react'
import { MdAdd, MdPerson, MdWork, MdSchedule, MdWarning } from 'react-icons/md'
import { PilotCard } from '../../components/cards'
import { Button, LoadingSpinner, EmptyState, SearchBar, FilterBar } from '../../components/ui'
import { usePilot } from '../../hooks/usePilot'

function PilotList({ onViewPilot, onEditPilot, onDeletePilot }) {
  const [pilots, setPilots] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // all, active, available, on-leave
  const [isLoading, setIsLoading] = useState(true)

  const { getPilotsAsync, deletePilotAsync } = usePilot()

  const loadPilots = useCallback(async () => {
    try {
      setIsLoading(true)
      const pilotsData = await getPilotsAsync()
      setPilots(pilotsData || [])
    } catch (error) {
      console.error('Error loading pilots:', error)
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadPilots()
  }, [loadPilots])

  const handleDeletePilot = async (pilot) => {
    try {
      await deletePilotAsync(pilot.number)
      await loadPilots() // Refresh the list
      if (onDeletePilot) {
        onDeletePilot(pilot)
      }
    } catch (error) {
      console.error('Error deleting pilot:', error)
    }
  }

  const handleViewPilot = (pilot) => {
    if (onViewPilot) {
      onViewPilot(pilot)
    }
  }

  const handleEditPilot = (pilot) => {
    if (onEditPilot) {
      onEditPilot(pilot)
    }
  }

  const getFilteredPilots = () => {
    let filteredPilots = pilots

    if (filter === 'active') {
      filteredPilots = pilots.filter((pilot) => pilot.status === 'active')
    } else if (filter === 'available') {
      filteredPilots = pilots.filter((pilot) => pilot.status === 'available')
    } else if (filter === 'on-leave') {
      filteredPilots = pilots.filter((pilot) => pilot.status === 'on-leave')
    }

    if (searchTerm) {
      filteredPilots = filteredPilots.filter(
        (pilot) =>
          `${pilot.firstName} ${pilot.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilot.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilot.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilot.status?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filteredPilots
  }

  const filteredPilots = getFilteredPilots()

  const getActiveCount = () => pilots.filter((pilot) => pilot.status === 'active').length
  const getAvailableCount = () => pilots.filter((pilot) => pilot.status === 'available').length
  const getOnLeaveCount = () => pilots.filter((pilot) => pilot.status === 'on-leave').length
  const getTotalCount = () => pilots.length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with search and filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search pilots..."
        />

        <div className="flex items-center space-x-3">
          <FilterBar
            filterValue={filter}
            onFilterChange={(e) => setFilter(e.target.value)}
            filterOptions={[
              { value: 'all', label: 'All Pilots' },
              { value: 'active', label: `Active (${getActiveCount()})` },
              { value: 'available', label: `Available (${getAvailableCount()})` },
              { value: 'on-leave', label: `On Leave (${getOnLeaveCount()})` },
            ]}
          />

          <Button
            variant="primary"
            size="md"
            onClick={() => {
              /* TODO: Implement create pilot */
            }}
            className="flex items-center space-x-2"
          >
            <MdAdd className="text-lg" />
            <span>Add Pilot</span>
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Total Pilots
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getTotalCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <MdPerson className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Active Pilots
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getActiveCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
              <MdWork className="text-success-600 dark:text-success-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">
                Available
              </p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getAvailableCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <MdSchedule className="text-blue-600 dark:text-blue-400 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">On Leave</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-white mt-1">
                {getOnLeaveCount()}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900 rounded-lg flex items-center justify-center">
              <MdWarning className="text-warning-600 dark:text-warning-400 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Pilot Grid */}
      {filteredPilots.length === 0 ? (
        <EmptyState
          icon={<MdPerson className="text-4xl text-neutral-400" />}
          title="No pilots found"
          description={
            searchTerm || filter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by adding your first pilot'
          }
          action={
            <Button
              variant="primary"
              onClick={() => {
                /* TODO: Implement create pilot */
              }}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Pilot</span>
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPilots.map((pilot) => (
            <PilotCard
              key={pilot.number}
              pilot={pilot}
              onView={() => handleViewPilot(pilot)}
              onEdit={() => handleEditPilot(pilot)}
              onDelete={() => handleDeletePilot(pilot)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default PilotList
